import axios from "axios";

// Central API base URL used by every frontend API file.
function detectProductionApiBaseUrl() {
  const sameOriginScript = [...document.scripts]
    .map((script) => script.src)
    .find((src) => {
      try {
        const url = new URL(src, window.location.href);
        return (
          url.origin === window.location.origin &&
          url.pathname.includes("/static/js/")
        );
      } catch (_) {
        return false;
      }
    });

  if (sameOriginScript) {
    const scriptUrl = new URL(sameOriginScript, window.location.href);
    const appPath = scriptUrl.pathname
      .split("/static/js/")[0]
      .replace(/\/$/, "");
    return `${window.location.origin}${appPath}/backend/api`;
  }

  const directory = window.location.pathname.endsWith("/")
    ? window.location.pathname
    : window.location.pathname.replace(/\/[^/]*$/, "/");
  return `${window.location.origin}${directory.replace(/\/$/, "")}/backend/api`;
}

const DEFAULT_API_BASE_URL =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
    // Keep the hostname identical to the React dev server. localhost and
    // 127.0.0.1 have separate cookies, which otherwise looks like a logout.
    ? `http://${window.location.hostname}/kaamdesk/backend/api`
    : detectProductionApiBaseUrl();

// Environment variable can override the default backend path.
export const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || DEFAULT_API_BASE_URL
).replace(/\/$/, "");

// CSRF token key used by the existing PHP backend flow.
export const CSRF_STORAGE_KEY = "kaamdesk_csrf_token";

// Store CSRF token from backend responses for protected PHP requests.
export function rememberCsrfToken(data) {
  if (data?.csrf_token) {
    window.sessionStorage.setItem(CSRF_STORAGE_KEY, data.csrf_token);
  }
  return data;
}

// Clear CSRF token when logout or local session clear happens.
export function clearCsrfToken() {
  window.sessionStorage.removeItem(CSRF_STORAGE_KEY);
}

// Single Axios instance for all frontend API calls.
const axiosApi = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 20000,
  responseType: "text",
  transformResponse: [(data) => data],
  validateStatus: () => true,
});

// Notify the app once when an authenticated PHP session is no longer valid.
// This prevents dashboard polling from continuing to hit every protected API
// and filling the browser console with repeated 401 responses.
let sessionExpiredEventSent = false;
function notifySessionExpired(response) {
  if (response?.status !== 401 || sessionExpiredEventSent) return;

  const requestUrl = String(response?.config?.url || "");
  // Registration is a public multi-step flow. A temporary registration-session
  // 401 must be handled by that flow itself and must never force the whole app
  // to navigate back to the login screen.
  if (response?.config?.suppressSessionExpired || requestUrl.includes("/login/") || requestUrl.includes("/registration/")) return;

  sessionExpiredEventSent = true;
  window.dispatchEvent(new CustomEvent("kaamdesk:session-expired"));
  window.setTimeout(() => {
    sessionExpiredEventSent = false;
  }, 1000);
}

// Some shared-hosting firewalls answer the first API request with a tiny
// JavaScript cookie challenge instead of JSON. Browser pages execute it, but
// XHR/Axios responses do not, so complete that specific challenge and retry.
function readHostingCookieChallenge(responseText) {
  if (
    typeof responseText !== "string" ||
    !responseText.includes("document.cookie")
  )
    return null;

  const match = responseText.match(
    /document\.cookie\s*=\s*["'](humans_[A-Za-z0-9_]+)=([^;"']+)(?:;[^"']*)?["']\s*;/i,
  );

  return match ? { name: match[1], value: match[2] } : null;
}

// Attach only the same JSON and CSRF headers that frontend4 used.
axiosApi.interceptors.request.use((config) => {
  const method = (config.method || "get").toUpperCase();
  const csrfToken = window.sessionStorage.getItem(CSRF_STORAGE_KEY);
  const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

  config.headers = {
    Accept: "application/json",
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(method !== "GET" && csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    ...(config.headers || {}),
  };
  if (isFormData) {
    delete config.headers["Content-Type"];
    delete config.headers["content-type"];
  }

  return config;
});

// Convert Axios network and timeout errors into frontend4-style user-facing errors.
axiosApi.interceptors.response.use(
  async (response) => {
    notifySessionExpired(response);

    const challenge = readHostingCookieChallenge(response.data);
    if (!challenge || response.config._hostingChallengeRetried) return response;

    document.cookie = `${challenge.name}=${challenge.value}; path=/; SameSite=Lax`;
    response.config._hostingChallengeRetried = true;
    return axiosApi.request(response.config);
  },
  (error) => {
    if (error?.code === "ECONNABORTED" || error?.name === "AbortError") {
      throw new Error(
        error?.config?.timeoutMessage || "Request timed out. Please try again.",
      );
    }
    throw new Error("Could not connect to the server. Please try again.");
  },
);

// Parse raw text response so PHP warnings still produce the same helpful JSON error message.
export function parseJsonResponse(responseText) {
  try {
    return responseText ? JSON.parse(responseText) : {};
  } catch (error) {
    if (readHostingCookieChallenge(responseText)) {
      throw new Error(
        "Server security verification could not complete. Please refresh the page and try again.",
      );
    }
    throw new Error(
      `Backend did not return valid JSON. Check PHP error: ${String(responseText).slice(0, 180)}`,
    );
  }
}

// Run one API request through the centralized Axios instance and keep old success/error behavior.
let csrfRefreshPromise = null;

function isCsrfExpiredResponse(response, data) {
  if (response?.status !== 403) return false;
  return /security token expired/i.test(String(data?.message || ""));
}

function isCsrfRefreshExcludedPath(path) {
  const normalizedPath = String(path || "").toLowerCase();
  return normalizedPath.includes("/login/") || normalizedPath.includes("/registration/");
}

async function refreshCsrfTokenSilently() {
  if (csrfRefreshPromise) return csrfRefreshPromise;

  csrfRefreshPromise = (async () => {
    const { response, data } = await getSessionJson(
      "/dashboard/session.php",
      10000,
      true,
    );

    if (response.status >= 200 && response.status < 300 && data?.csrf_token) {
      return data.csrf_token;
    }

    throw new Error("CSRF refresh failed.");
  })().finally(() => {
    csrfRefreshPromise = null;
  });

  return csrfRefreshPromise;
}

export async function requestJson(path, options = {}, settings = {}) {
  const method = (options.method || "GET").toUpperCase();
  const response = await axiosApi.request({
    url: path,
    method,
    data: options.body,
    headers: options.headers,
    timeout: settings.timeoutMs || 20000,
    timeoutMessage:
      settings.timeoutMessage ||
      "Request timed out. Please check your connection and try again.",
  });

  const data = parseJsonResponse(response.data);

  // A PHP session can be restored/regenerated after the browser tab has been
  // idle or after hosting infrastructure has recreated its session file. That
  // legitimately rotates the session-bound CSRF token. Recover this transient
  // mismatch silently: obtain the current token once, then retry the original
  // request exactly once. This prevents harmless background POSTs from showing
  // "Security token expired" to an otherwise authenticated user.
  if (
    method !== "GET" &&
    !settings._csrfRetried &&
    !isCsrfRefreshExcludedPath(path) &&
    isCsrfExpiredResponse(response, data)
  ) {
    try {
      await refreshCsrfTokenSilently();
      return requestJson(path, options, { ...settings, _csrfRetried: true });
    } catch (_) {
      // If the session itself is no longer recoverable, fall through to the
      // original error so a genuine authentication problem is not hidden.
    }
  }

  if (
    !response.status ||
    response.status < 200 ||
    response.status >= 300 ||
    data.success === false
  ) {
    const detail =
      settings.includeErrorDetail && data.error ? ` ${data.error}` : "";
    const statusDetail = response.status ? ` (HTTP ${response.status})` : "";
    const requestError = new Error(
      `${data.message || settings.defaultMessage || `API request failed${statusDetail}.`}${detail}`,
    );
    requestError.code = data.code;
    requestError.activeSession = data.active_session || null;
    if (settings.attachValidationErrors)
      requestError.errors = data.errors || {};
    throw requestError;
  }

  // Successful business mutations can create a notification on the server.
  // Wake the notification layer immediately instead of waiting for its 60-second
  // fallback poll. The realtime SSE transport handles the normal case; this
  // event is only used by the notification hook when SSE is unavailable.
  if (method !== "GET") {
    const normalizedPath = String(path || "").toLowerCase();
    const isNotificationInternal = normalizedPath.includes("/notifications/");
    const isAuthFlow =
      normalizedPath.includes("/login/") ||
      normalizedPath.includes("/registration/") ||
      normalizedPath.includes("/dashboard/logout");
    const isTeamChat = normalizedPath.includes("/teamchat/");
    if (!isNotificationInternal && !isAuthFlow && !isTeamChat) {
      window.dispatchEvent(new CustomEvent("kaamdesk:business-mutation-success"));
    }
  }

  return rememberCsrfToken(data);
}

// Shortcut for POST JSON endpoints; login and registration had this timeout message in frontend4.
export function postJson(path, payload, settings = {}) {
  return requestJson(
    path,
    { method: "POST", body: JSON.stringify(payload) },
    {
      timeoutMessage: "Request timed out. Please try again.",
      ...settings,
    },
  );
}

// Read session responses without throwing on 401, because login flow treats 401 as logged out.
export async function getSessionJson(path, timeoutMs = 10000, suppressSessionExpired = false) {
  const response = await axiosApi.get(path, {
    timeout: timeoutMs,
    timeoutMessage: "Request timed out. Please try again.",
    suppressSessionExpired,
  });
  let data = {};
  try {
    data = response.data ? JSON.parse(response.data) : {};
  } catch (error) {
    data = {};
  }
  return { response, data: rememberCsrfToken(data) };
}

export default axiosApi;
